import { useTranslation } from "react-i18next"
import { Tag } from "@/components/ui/tag"
import { cn } from "@/lib/utils"
import { BetterDocExchangeLink } from "@/modules/admin/better-doc/components/better-doc-exchange-link"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { FAILURE_STEP_TYPES } from "@/modules/admin/better-doc/lib/better-doc-tag-variants"
import { formatBetterDocDateTime } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { BetterDocSessionStep } from "@/modules/admin/better-doc/services/betterDocSessionQuery"

type BetterDocStepTimelineProps = {
  steps: ReadonlyArray<BetterDocSessionStep>
}

export const BetterDocStepTimeline = ({ steps }: BetterDocStepTimelineProps) => {
  const { t } = useTranslation()

  if (steps.length === 0) {
    return (
      <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-step-timeline-empty">
        {t("admin.betterDoc.stepTimeline.empty")}
      </p>
    )
  }

  return (
    <div className="flex flex-col" data-testid="better-doc-admin-step-timeline">
      {steps.map((step) => (
        <div key={step.stepId} className="group flex gap-4" data-testid="better-doc-admin-step">
          <p className="text-on-surface-subdued w-40 shrink-0 py-1 text-sm">
            {formatBetterDocDateTime(step.occurredAt)}
          </p>
          <div
            className={cn(
              "w-6 shrink-0 self-stretch group-first:rounded-t-full group-last:rounded-b-full group-only:h-[calc(100%-4px)]",
              FAILURE_STEP_TYPES.includes(step.type) ? "bg-critical-surface" : "bg-lilac-surface-subdued",
            )}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1 py-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-on-surface text-sm font-medium" data-testid="better-doc-admin-step-type">
                {getBetterDocEnumLabel(t, "sessionStepType", step.type)}
              </span>
              {step.betterDocState === null ? null : (
                <Tag
                  hasIcon={false}
                  label={getBetterDocEnumLabel(t, "recommendationState", step.betterDocState)}
                  data-testid="better-doc-admin-step-state"
                />
              )}
              {step.isUnchanged ? <Tag hasIcon={false} label={t("admin.betterDoc.stepTimeline.unchanged")} /> : null}
              <span className="text-on-surface-subdued text-xs">{step.actor}</span>
              {step.exchangeId === null ? null : <BetterDocExchangeLink exchangeId={step.exchangeId} />}
            </div>
            {step.detail === null ? null : (
              <p className="text-on-surface-subdued text-sm wrap-break-word" data-testid="better-doc-admin-step-detail">
                {step.detail}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
